/** Ported from wanderu-component-chart/src/utils.js */

import {
    COLUMN_SCALING_FACTOR,
    GRAPH_OFFSET,
    MAX_COLUMN_WIDTH,
    SMOOTHING_FACTOR,
    VERTICAL_OFFSET,
} from './chart-constants'

export interface DataPoint {
    xCoord: number
    yCoord: number
    xVal: string | number
    yVal: number
}

export interface TooltipData {
    xPos: number
    xVal: string | number
    yPos: number
    yVal: number
}

export interface ChartData {
    columnWidth: number
    dataPoints: Record<string | number, DataPoint>
    scalingFactorX: number
    scalingFactorY: number
    verticalAxisLabels: number[]
}

interface ChartOpts {
    data: { x: string | number; y: number }[]
    containerWidth: number
    containerHeight: number
    numVerticalTicks: number
    roundingFactor: number
}

function getOpposedLine(pointA: DataPoint, pointB: DataPoint) {
    const lengthX = pointB.xCoord - pointA.xCoord
    const lengthY = pointB.yCoord - pointA.yCoord

    return {
        length: Math.sqrt(lengthX ** 2 + lengthY ** 2),
        angle: Math.atan2(lengthY, lengthX),
    }
}

function getControlPoints(
    current: DataPoint,
    previous: DataPoint | undefined,
    next: DataPoint | undefined,
    reverse?: boolean,
): [number, number] {
    const opposedLine = getOpposedLine(previous ?? current, next ?? current)

    const angle = opposedLine.angle + (reverse ? Math.PI : 0)
    const length = opposedLine.length * SMOOTHING_FACTOR

    const x = current.xCoord + Math.cos(angle) * length
    const y = current.yCoord + Math.sin(angle) * length
    return [x, y]
}

/**
 * Parses a `YYYY-MM-DD` date string — a naive calendar date, as WAPI and the warehouse
 * quote them, never a UTC timestamp — into a `Date` built from explicit Y/M/D parts
 * **at UTC**.
 *
 * Build at UTC, read at UTC (→ invariants.md#ssr-safe-rendered-dates). Every caller
 * formats this through `localeFormatter`, whose `dateTime` is pinned to UTC, so building
 * at LOCAL midnight instead would shift the calendar day for every visitor east of UTC:
 * a Feb 1 tick renders "Feb" on the UTC container's SSR pass and "Jan" in the browser in
 * Berlin — a wrong label for those visitors and a React #418 on the price-history chart,
 * whose axes are not mount-gated. Do NOT switch to `new Date(year, month - 1, day)` (a
 * local-midnight instant) or to `new Date(dateStr)`; the zone-invariance suite in
 * `chart-utils.test.ts` fails on either.
 */
export function parseDateString(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day))
}

export function drawCurve(point: DataPoint, index: number, arr: DataPoint[]): string {
    const controlPointStart = getControlPoints(arr[index - 1], arr[index - 2], point)
    const controlPointEnd = getControlPoints(point, arr[index - 1], arr[index + 1], true)

    const { xCoord: x, yCoord: y } = point
    const [startX, startY] = controlPointStart
    const [endX, endY] = controlPointEnd
    return `C ${startX},${startY} ${endX},${endY} ${x},${y}`
}

export function drawLine(point: DataPoint): string {
    const { xCoord: x, yCoord: y } = point
    return `L ${x} ${y}`
}

function range(n: number): number[] {
    return Array.from({ length: n }, (_, i) => i)
}

export function calculateChartData(chartOpts: ChartOpts): ChartData {
    const { data, containerWidth, containerHeight, numVerticalTicks, roundingFactor } = chartOpts

    const maxValueX = data.length
    const maxValueY = Math.max(...data.map((d) => d.y))
    const roundedMaxY = Math.ceil(maxValueY / roundingFactor) * roundingFactor
    const scalingFactorX = (containerWidth - GRAPH_OFFSET) / maxValueX

    const incrementValue =
        Math.ceil(roundedMaxY / numVerticalTicks / roundingFactor) * roundingFactor

    const verticalAxisLabels = range(numVerticalTicks + 1)
        .map((num) => num * incrementValue)
        .reverse()

    const maxVerticalY = Math.max(...verticalAxisLabels)
    const scalingFactorY = containerHeight / maxVerticalY

    const dataPoints: Record<string | number, DataPoint> = {}
    for (let index = 0; index < data.length; index++) {
        const obj = data[index]
        const scaledX = index * scalingFactorX
        const scaledY = obj.y * scalingFactorY

        const xCoord = scaledX + GRAPH_OFFSET * COLUMN_SCALING_FACTOR
        const yCoord = containerHeight - scaledY + VERTICAL_OFFSET

        dataPoints[obj.x] = { xCoord, yCoord, xVal: obj.x, yVal: obj.y }
    }

    const columnWidth =
        scalingFactorX > MAX_COLUMN_WIDTH
            ? MAX_COLUMN_WIDTH
            : scalingFactorX * COLUMN_SCALING_FACTOR

    return {
        columnWidth,
        dataPoints,
        scalingFactorX,
        scalingFactorY,
        verticalAxisLabels,
    }
}
